import { Alert, Group, Stack } from "@mantine/core"
import { FileWithPath, MIME_TYPES } from "@mantine/dropzone"
import { IconChecks } from "@tabler/icons-react"
import {
  Button,
  Card,
  DEFAULT_MAX_SIZE,
  DocumentUploader,
  FileListItem,
  Loader,
} from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { IngestionSettings } from "~/pages/Admin/Ingestion/ProductImport/components/IngestionSettings"
import { useFindIngestionSettingsQuery } from "~/pages/Admin/Ingestion/ProductImport/hooks/useFindIngestionSettings.ts"
import { useVerifyBlankProductImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useVerifyBlankProductImportMutation"
import { useVerifyProductImportMutation } from "~/pages/Admin/Ingestion/ProductImport/hooks/useVerifyProductImportMutation"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export function IngestionDocumentValidate() {
  const { t } = useTranslation()
  const [owner, setOwner] = useState<string>("")
  const [selectedFile, setSelectedFile] = useState<File>()

  const { mutate: verifyProductImport, isPending: isVerifyingProductImport } =
    useVerifyProductImportMutation({
      onSuccess: () => {
        showSuccessNotification({
          title: "File uploaded",
          message: "File is being validated",
        })
        setSelectedFile(undefined)
      },
      onError: (error) => {
        showAxiosError({
          notificationTitle: "Something went wrong",
          notificationMessage: "File non validated",
          showNotification: true,
          error,
        })
      },
    })

  const {
    mutate: verifyBlankProductImport,
    isPending: isVerifyingBlankProductImport,
  } = useVerifyBlankProductImportMutation({
    onSuccess: () => {
      showSuccessNotification({
        title: "Blank file validated",
        message: "The blank file has been validated successfully",
      })
    },
    onError: (error) => {
      showAxiosError({
        notificationTitle: "Something went wrong",
        notificationMessage: "File non validated",
        showNotification: true,
        error,
      })
    },
  })

  const { data: ingestionSettings, isPending: isLoading } =
    useFindIngestionSettingsQuery()
  const [isBlankIngestion, setIsBlankIngestion] = useState<boolean>(false)

  const handleDrop = (files: FileWithPath[]) => {
    setSelectedFile(files[0])
  }

  if (!ingestionSettings) {
    return <Alert>No ingestion settings found</Alert>
  }

  const handleSubmit = () => {
    if (!selectedFile) {
      return
    }
    if (isBlankIngestion) {
      verifyBlankProductImport({ file: selectedFile, clientUserEmail: owner })
      return
    }
    verifyProductImport({ file: selectedFile, clientUserEmail: owner })
  }

  if (isVerifyingProductImport || isVerifyingBlankProductImport) {
    return <Loader />
  }

  return (
    <Card style={{ justifyContent: "space-between" }}>
      <Group align="stretch" style={{ width: "100%" }} wrap="nowrap">
        <Stack
          px="sm"
          h="auto"
          w="100%"
          style={{ flexBasis: "40%" }}
          justify="space-between"
        >
          <Group h="70%" w="100%">
            {selectedFile ? (
              <FileListItem
                fileName={selectedFile.name}
                onRemove={() => setSelectedFile(undefined)}
                w="100%"
                h="100%"
                size="lg"
              />
            ) : (
              <DocumentUploader
                onDrop={handleDrop}
                maxFiles={1}
                width="100%"
                maxSize={10 * 1024 ** 2}
                accept={[MIME_TYPES.csv]}
                title={t("documentUpload.title")}
                helpText={t("documentUpload.helptext", {
                  maxSize: `${DEFAULT_MAX_SIZE / 1024 / 1024}`,
                })}
              />
            )}
          </Group>
          <Group>
            <Button
              display="inline-block"
              fullWidth={true}
              variant="primary"
              onClick={handleSubmit}
              disabled={!(selectedFile && owner)}
              icon={<IconChecks />}
            >
              Validate File
            </Button>
          </Group>
        </Stack>
        {isLoading ? (
          <Loader />
        ) : ingestionSettings ? (
          <IngestionSettings
            mode="verify"
            selectedFile={selectedFile}
            ingestionSettings={ingestionSettings.data}
            isBlankIngestion={isBlankIngestion}
            setIsBlankIngestion={setIsBlankIngestion}
            owner={owner}
            setOwner={setOwner}
          />
        ) : null}
      </Group>
    </Card>
  )
}
